import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../hooks/useInventoryOperations';
import api from '../services/api';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

// ─── Inventory Value by Category ────────────────────────────────────────────
const CategoryValueReport = () => {
  const { getValueReport } = useInventory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getValueReport().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-4"><div className="spinner-border text-primary" /></div>;
  if (!data) return <p className="text-muted">No data available.</p>;

  const sorted = [...(data.items_by_category || [])].sort((a, b) => b.total_value - a.total_value);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Inventory Value by Category</h5>
        <span className="fw-bold fs-5">{formatCurrency(data.total_inventory_value)}</span>
      </div>
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Category</th>
              <th className="text-end">Items</th>
              <th className="text-end">Total Value</th>
              <th className="text-end">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((cat, i) => (
              <tr key={i}>
                <td><strong>{cat.category_name}</strong></td>
                <td className="text-end">{cat.item_count}</td>
                <td className="text-end">{formatCurrency(cat.total_value)}</td>
                <td className="text-end text-muted">
                  {data.total_inventory_value > 0
                    ? ((cat.total_value / data.total_inventory_value) * 100).toFixed(1) + '%'
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="table-light fw-bold">
            <tr>
              <td>Total</td>
              <td className="text-end">{sorted.reduce((s, c) => s + c.item_count, 0)}</td>
              <td className="text-end">{formatCurrency(data.total_inventory_value)}</td>
              <td className="text-end">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Inventory On Hand ───────────────────────────────────────────────────────
const OnHandReport = () => {
  const { getSummary } = useInventory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getSummary(0, 1000).then(r => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(i =>
    i.item_name.toLowerCase().includes(search.toLowerCase()) ||
    i.category_name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="text-center py-4"><div className="spinner-border text-primary" /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Inventory On Hand</h5>
        <input
          type="text"
          className="form-control form-control-sm w-auto"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 200 }}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th className="text-end">Qty On Hand</th>
              <th>Unit</th>
              <th className="text-end">Avg Unit Price</th>
              <th className="text-end">Total Value</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="text-center text-muted py-3">No items found.</td></tr>
            ) : filtered.map((item, i) => (
              <tr key={i}>
                <td><strong>{item.item_name}</strong></td>
                <td><span className="badge bg-info">{item.category_name}</span></td>
                <td className="text-end">{item.total_quantity}</td>
                <td>{item.unit_of_measure}</td>
                <td className="text-end">{formatCurrency(item.average_unit_price)}</td>
                <td className="text-end">{formatCurrency(item.total_value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="table-light fw-bold">
            <tr>
              <td colSpan="2">Total</td>
              <td className="text-end">{filtered.reduce((s, i) => s + i.total_quantity, 0)}</td>
              <td></td>
              <td></td>
              <td className="text-end">{formatCurrency(filtered.reduce((s, i) => s + parseFloat(i.total_value || 0), 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Low Stock / Reorder Alerts ──────────────────────────────────────────────
const LowStockReport = () => {
  const { getSummary } = useInventory();
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSummary(0, 1000).then(r => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const lowStock = items.filter(i => i.total_quantity <= threshold).sort((a, b) => a.total_quantity - b.total_quantity);

  if (loading) return <div className="text-center py-4"><div className="spinner-border text-primary" /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Low Stock / Reorder Alerts</h5>
        <div className="d-flex align-items-center gap-2">
          <label className="form-label mb-0 small">Threshold:</label>
          <input
            type="number"
            className="form-control form-control-sm"
            value={threshold}
            onChange={e => setThreshold(parseInt(e.target.value) || 0)}
            style={{ width: 80 }}
            min={0}
          />
        </div>
      </div>
      {lowStock.length === 0 ? (
        <div className="alert alert-success">All items are above the threshold of {threshold} units.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm">
            <thead className="table-light">
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th className="text-end">Qty On Hand</th>
                <th>Unit</th>
                <th className="text-end">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {lowStock.map((item, i) => (
                <tr key={i} className={item.total_quantity === 0 ? 'table-danger' : 'table-warning'}>
                  <td><strong>{item.item_name}</strong></td>
                  <td><span className="badge bg-info">{item.category_name}</span></td>
                  <td className="text-end fw-bold">{item.total_quantity}</td>
                  <td>{item.unit_of_measure}</td>
                  <td className="text-end">{formatCurrency(item.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Transaction History ─────────────────────────────────────────────────────
const TransactionHistoryReport = () => {
  const { getTransactions } = useInventory();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState('');

  const fetch = async () => {
    setLoading(true);
    const params = { skip: 0, limit: 100 };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (type) params.transaction_type = type;
    const r = await getTransactions(params);
    setTransactions(r.data || []);
    setTotal(r.total || 0);
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Transaction History</h5>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <input type="date" className="form-control form-control-sm" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: 145 }} />
          <input type="date" className="form-control form-control-sm" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: 145 }} />
          <select className="form-select form-select-sm" value={type} onChange={e => setType(e.target.value)} style={{ width: 130 }}>
            <option value="">All Types</option>
            <option value="credit">Credit (Add)</option>
            <option value="debit">Debit (Allocate)</option>
          </select>
          <button className="btn btn-sm btn-primary" onClick={fetch}>Apply</button>
        </div>
      </div>
      {loading ? <div className="text-center py-4"><div className="spinner-border text-primary" /></div> : (
        <>
          <p className="text-muted small mb-2">Showing {transactions.length} of {total} transactions</p>
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Item</th>
                  <th>Job</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Unit Price</th>
                  <th className="text-end">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan="7" className="text-center text-muted py-3">No transactions found.</td></tr>
                ) : transactions.map((t, i) => (
                  <tr key={i}>
                    <td className="small">{formatDate(t.created_at)}</td>
                    <td>
                      <span className={`badge ${t.transaction_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                        {t.transaction_type.toUpperCase()}
                      </span>
                    </td>
                    <td>{t.item?.name}</td>
                    <td className="small">{t.job?.name || <span className="text-muted">—</span>}</td>
                    <td className="text-end">{t.transaction_type === 'credit' ? '+' : '-'}{t.quantity}</td>
                    <td className="text-end">{formatCurrency(t.unit_price)}</td>
                    <td className="text-end fw-bold">{formatCurrency(t.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Job Cost Summary ────────────────────────────────────────────────────────
const JobCostReport = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get('/inventory/reports/job-cost')
      .then(r => setJobs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="text-center py-4"><div className="spinner-border text-primary" /></div>;
  if (jobs.length === 0) return <p className="text-muted">No job allocations found.</p>;

  const grandTotal = jobs.reduce((s, j) => s + j.total_value, 0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Job Cost Summary</h5>
        <span className="fw-bold fs-5">{formatCurrency(grandTotal)}</span>
      </div>
      <div className="table-responsive">
        <table className="table table-hover table-sm">
          <thead className="table-light">
            <tr>
              <th>Job</th>
              <th>Status</th>
              <th className="text-end">Total Units</th>
              <th className="text-end">Total Cost</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <React.Fragment key={job.job_id}>
                <tr className="fw-bold">
                  <td>{job.job_name}</td>
                  <td>
                    <span className={`badge ${job.job_status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                      {job.job_status.charAt(0).toUpperCase() + job.job_status.slice(1)}
                    </span>
                  </td>
                  <td className="text-end">{job.total_quantity}</td>
                  <td className="text-end">{formatCurrency(job.total_value)}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => toggle(job.job_id)}>
                      {expanded[job.job_id] ? 'Hide' : 'Details'}
                    </button>
                  </td>
                </tr>
                {expanded[job.job_id] && job.items.map((item, i) => (
                  <tr key={`${job.job_id}-${i}`} className="table-light text-muted small">
                    <td className="ps-4">{item.item_name}</td>
                    <td></td>
                    <td className="text-end">{item.total_quantity}</td>
                    <td className="text-end">{formatCurrency(item.total_value)}</td>
                    <td></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="table-light fw-bold">
            <tr>
              <td colSpan="2">Grand Total</td>
              <td className="text-end">{jobs.reduce((s, j) => s + j.total_quantity, 0)}</td>
              <td className="text-end">{formatCurrency(grandTotal)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Main Reports Page ───────────────────────────────────────────────────────
const TABS = [
  { key: 'category-value', label: 'Value by Category', icon: 'bi-pie-chart', component: CategoryValueReport },
  { key: 'on-hand', label: 'Inventory On Hand', icon: 'bi-boxes', component: OnHandReport },
  { key: 'low-stock', label: 'Low Stock', icon: 'bi-exclamation-triangle', component: LowStockReport },
  { key: 'transactions', label: 'Transaction History', icon: 'bi-journal-text', component: TransactionHistoryReport },
  { key: 'job-cost', label: 'Job Cost Summary', icon: 'bi-briefcase', component: JobCostReport },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('category-value');
  const ActiveComponent = TABS.find(t => t.key === activeTab)?.component;

  return (
    <div className="container-fluid py-4">
      <h1 className="mb-4">Reports</h1>

      {/* Special Reports Section */}
      <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
        <i className="bi bi-calculator-fill fs-4 me-3"></i>
        <div className="flex-grow-1">
          <h5 className="alert-heading mb-1">Inventory Value Report</h5>
          <p className="mb-0">Calculate inventory value as of a specific date or compare between two dates for tax, financial reporting, and period analysis.</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/reports/inventory-value')}
        >
          <i className="bi bi-file-earmark-text me-2"></i>
          Generate Report
        </button>
      </div>

      <ul className="nav nav-tabs mb-4">
        {TABS.map(tab => (
          <li className="nav-item" key={tab.key}>
            <button
              className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`bi ${tab.icon} me-2`}></i>{tab.label}
            </button>
          </li>
        ))}
      </ul>
      <div className="card shadow-sm">
        <div className="card-body">
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
